import { coerceBooleanProperty } from '@angular/cdk/coercion';
import * as i0 from '@angular/core';
import { inject, ChangeDetectorRef, EventEmitter, Component, ChangeDetectionStrategy, ViewChild, Input, HostBinding, Output, HostListener, NgModule } from '@angular/core';
import { NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';

class SiSearchBarComponent {
    inputRef;
    _value = '';
    cdRef = inject(ChangeDetectorRef);
    debouncer = new Subject();
    /**
     * Time unit change of search input takes effect.
     */
    debounceTime = 400;
    /**
     * Prohibited characters restricting search.
     */
    prohibitedCharacters;
    /**
     * Define search input placeholder.
     */
    placeholder = '';
    /**
     * Display search icon before search input.
     */
    showIcon;
    /**
     * Whether the search is tabbable or not.
     */
    tabbable = true;
    /**
     * Define search input content.
     */
    get value() {
        return this._value;
    }
    /**
     * @defaultref {@link _value}
     */
    set value(value) {
        if (value !== this._value) {
            this.writeValue(value);
            this.onChange(this._value);
            this.onTouch();
            this.searchChange.emit(this._value);
        }
    }
    /**
     * Disable component
     */
    get disabled() {
        return this.inputRef.nativeElement.disabled;
    }
    set disabled(value) {
        this.setDisabledState(coerceBooleanProperty(value));
    }
    _readonly = false;
    get readonly() {
        return this._readonly;
    }
    /**
     * @defaultref {@link _readonly}
     */
    set readonly(value) {
        this._readonly = coerceBooleanProperty(value);
    }
    /**
     * Color to use for component background
     */
    colorVariant = 'base-1';
    /**
     * Output callback event will provide you with search term if search input changes.
     */
    searchChange = new EventEmitter();
    isInvalid = false;
    inFocus = false;
    onChange = (val) => { };
    onTouch = () => { };
    /** @internal */
    writeValue(value) {
        this._value = value ?? '';
        this.inputRef.nativeElement.value = this._value;
        this.cdRef.markForCheck();
    }
    /** @internal */
    registerOnChange(fn) {
        this.onChange = fn;
    }
    /** @internal */
    registerOnTouched(fn) {
        this.onTouch = fn;
    }
    /** @internal */
    setDisabledState(isDisabled) {
        this.inputRef.nativeElement.disabled = isDisabled;
    }
    ngOnInit() {
        this.debouncer.pipe(debounceTime(this.debounceTime)).subscribe(value => (this.value = value));
    }
    ngOnDestroy() {
        this.debouncer.complete();
    }
    resetForm(event) {
        event.stopPropagation();
        this.value = '';
    }
    isProhibitedCharactersUsed(searchString) {
        if (!this.prohibitedCharacters || !searchString) {
            return false;
        }
        for (const prohibitedCharacter of this.prohibitedCharacters) {
            if (searchString.includes(prohibitedCharacter)) {
                return true;
            }
        }
        return false;
    }
    /** @internal */
    focus() {
        this.inputRef.nativeElement.focus();
    }
    onCancelFocus(event) {
        event.stopPropagation();
    }
    input(event) {
        const value = event.target.value;
        if (!this.isProhibitedCharactersUsed(value)) {
            this.debouncer.next(value);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSearchBarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.5", type: SiSearchBarComponent, selector: "si-search-bar", inputs: { debounceTime: "debounceTime", prohibitedCharacters: "prohibitedCharacters", placeholder: "placeholder", showIcon: "showIcon", tabbable: "tabbable", value: "value", disabled: "disabled", readonly: "readonly", colorVariant: "colorVariant" }, outputs: { searchChange: "searchChange" }, host: { listeners: { "focus": "focus()" }, properties: { "class.readonly": "this._readonly" } }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: SiSearchBarComponent,
                multi: true
            }
        ], viewQueries: [{ propertyName: "inputRef", first: true, predicate: ["inputRef"], descendants: true, static: true }], ngImport: i0, template: "<div class=\"position-relative\" [class.disabled]=\"disabled\" [class.focus]=\"inFocus\">\n  <i *ngIf=\"showIcon\" class=\"search-bar-icon icon element-search text-secondary\"></i>\n  <input\n    #inputRef\n    type=\"text\"\n    class=\"form-control\"\n    [placeholder]=\"placeholder\"\n    [class.dark-background]=\"colorVariant === 'base-0'\"\n    [class.ps-9]=\"showIcon\"\n    [class.is-invalid]=\"isInvalid\"\n    [class.icon-end]=\"value\"\n    [attr.tabindex]=\"tabbable ? '' : '-1'\"\n    [readonly]=\"readonly\"\n    (focus)=\"inFocus = true\"\n    (blur)=\"inFocus = false\"\n    (input)=\"input($event)\"\n  />\n  <button\n    *ngIf=\"value\"\n    type=\"button\"\n    aria-label=\"clear\"\n    class=\"btn btn-circle btn-xs btn-ghost cancel-button element-cancel\"\n    [disabled]=\"disabled || readonly\"\n    (click)=\"resetForm($event)\"\n    (focusin)=\"onCancelFocus($event)\"\n  ></button>\n</div>\n", styles: [":host{--search-bar-icon-color: var(--element-text-secondary);--input-background-color: var(--element-base-1);--input-background-hover-color: var(--element-base-1)}:host div:hover .search-bar-icon,:host div:hover ::placeholder,:host div.focus .search-bar-icon,:host div.focus ::placeholder{color:var(--element-text-primary)!important}.dark-background{--input-background-color: var(--element-base-0);--input-background-hover-color: var(--element-ui-4)}input{border-color:var(--element-base-1)!important;transition:none;background-color:var(--input-background-color);border-radius:4px}input.form-control:focus-visible{background-color:var(--input-background-color);border-color:var(--input-background-color)!important}input.form-control:hover:not([disabled]){border-color:var(--element-ui-4)}.cancel-button{position:absolute;inset-block-start:4px;inset-inline-end:4px}.is-invalid~.cancel-button{color:var(--element-status-danger)}.search-bar-icon{position:absolute;inset-block-start:4px;inset-inline-start:4px;color:var(--search-bar-icon-color);line-height:1}.icon-end{padding-inline-end:1.5rem}::placeholder{color:var(--element-text-secondary);padding-inline-start:2px}.disabled .search-bar-icon,.disabled input,.disabled .cancel-button,.disabled ::placeholder{color:var(--element-text-disabled)}.disabled input{background-color:var(--input-background-color);border-color:var(--input-background-color)}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSearchBarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-search-bar', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: SiSearchBarComponent,
                            multi: true
                        }
                    ], changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"position-relative\" [class.disabled]=\"disabled\" [class.focus]=\"inFocus\">\n  <i *ngIf=\"showIcon\" class=\"search-bar-icon icon element-search text-secondary\"></i>\n  <input\n    #inputRef\n    type=\"text\"\n    class=\"form-control\"\n    [placeholder]=\"placeholder\"\n    [class.dark-background]=\"colorVariant === 'base-0'\"\n    [class.ps-9]=\"showIcon\"\n    [class.is-invalid]=\"isInvalid\"\n    [class.icon-end]=\"value\"\n    [attr.tabindex]=\"tabbable ? '' : '-1'\"\n    [readonly]=\"readonly\"\n    (focus)=\"inFocus = true\"\n    (blur)=\"inFocus = false\"\n    (input)=\"input($event)\"\n  />\n  <button\n    *ngIf=\"value\"\n    type=\"button\"\n    aria-label=\"clear\"\n    class=\"btn btn-circle btn-xs btn-ghost cancel-button element-cancel\"\n    [disabled]=\"disabled || readonly\"\n    (click)=\"resetForm($event)\"\n    (focusin)=\"onCancelFocus($event)\"\n  ></button>\n</div>\n", styles: [":host{--search-bar-icon-color: var(--element-text-secondary);--input-background-color: var(--element-base-1);--input-background-hover-color: var(--element-base-1)}:host div:hover .search-bar-icon,:host div:hover ::placeholder,:host div.focus .search-bar-icon,:host div.focus ::placeholder{color:var(--element-text-primary)!important}.dark-background{--input-background-color: var(--element-base-0);--input-background-hover-color: var(--element-ui-4)}input{border-color:var(--element-base-1)!important;transition:none;background-color:var(--input-background-color);border-radius:4px}input.form-control:focus-visible{background-color:var(--input-background-color);border-color:var(--input-background-color)!important}input.form-control:hover:not([disabled]){border-color:var(--element-ui-4)}.cancel-button{position:absolute;inset-block-start:4px;inset-inline-end:4px}.is-invalid~.cancel-button{color:var(--element-status-danger)}.search-bar-icon{position:absolute;inset-block-start:4px;inset-inline-start:4px;color:var(--search-bar-icon-color);line-height:1}.icon-end{padding-inline-end:1.5rem}::placeholder{color:var(--element-text-secondary);padding-inline-start:2px}.disabled .search-bar-icon,.disabled input,.disabled .cancel-button,.disabled ::placeholder{color:var(--element-text-disabled)}.disabled input{background-color:var(--input-background-color);border-color:var(--input-background-color)}\n"] }]
        }], propDecorators: { inputRef: [{
                type: ViewChild,
                args: ['inputRef', { static: true }]
            }], debounceTime: [{
                type: Input
            }], prohibitedCharacters: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], showIcon: [{
                type: Input
            }], tabbable: [{
                type: Input
            }], value: [{
                type: Input
            }], disabled: [{
                type: Input
            }], _readonly: [{
                type: HostBinding,
                args: ['class.readonly']
            }], readonly: [{
                type: Input
            }], colorVariant: [{
                type: Input
            }], searchChange: [{
                type: Output
            }], focus: [{
                type: HostListener,
                args: ['focus']
            }] } });

class SiSearchBarModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSearchBarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.5", ngImport: i0, type: SiSearchBarModule, declarations: [SiSearchBarComponent], imports: [CommonModule, ReactiveFormsModule], exports: [SiSearchBarComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSearchBarModule, imports: [CommonModule, ReactiveFormsModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiSearchBarModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, ReactiveFormsModule],
                    declarations: [SiSearchBarComponent],
                    exports: [SiSearchBarComponent]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SiSearchBarComponent, SiSearchBarModule };
